import React from "react";
import { Box, Grid, Typography, CircularProgress } from "@mui/joy";

/**
 * AdRender component that displays advertisements in an elevator-style layout
 *
 * To customize the ads, replace the placeholder URLs in the default AdData object with your own image URLs:
 * - leftImage: Image for the left side panel
 * - rightImage: Image for the right side panel
 * - bottomImage: Image for the bottom banner
 * - scrollingText: Text that scrolls across the bottom
 */

interface AdData {
	leftImage?: string;
	rightImage?: string;
	bottomImage?: string;
	scrollingText?: string;
}

export default function AdRender() {
	const [adData, setAdData] = React.useState<AdData>({});
	const [loading, setLoading] = React.useState(true);

	// Parse URL query string to get the id parameter
	const getQueryParam = (param: string): string | null => {
		const urlParams = new URLSearchParams(window.location.search);
		return urlParams.get(param);
	};

	// Fetch ad data from the database
	const fetchAdData = React.useCallback(async (id: string) => {
		try {
			setLoading(true);
			const response = await fetch("/Building/GetElevatorAd", {
				method: "POST",
				cache: "no-cache",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(id),
			});

			if (response.ok) {
				const text = await response.text();
				if (text && text !== "") {
					const data = JSON.parse(text);
					setAdData(data);
				} else {
					console.warn("No ad data found for building:", id);
					setAdData({});
				}
			} else {
				console.error("Failed to fetch ad data:", response.statusText);
				setAdData({});
			}
		} catch (error) {
			console.error("Error fetching ad data:", error);
			setAdData({});
		} finally {
			setLoading(false);
		}
	}, []);

	// Effect to parse URL and fetch data on component mount
	React.useEffect(() => {
		const id = getQueryParam("id");
		if (id) {
			fetchAdData(id);
		} else {
			console.warn("No id parameter found in URL query string");
			setLoading(false);
		}
	}, [fetchAdData]);

	if (loading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh"
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box sx={{ overflowY: "auto", padding: 0, display: "flex", flexDirection: "column", flex: 1, height: "100vh" }}>
			<Box sx={{ display: "flex", flexDirection: "row", flex: 1 }}>
				<Grid container spacing={0} sx={{ flex: 1, flexDirection: "column" }}>
					{/* Two boxes side by side */}
					<Grid xs={12} sx={{ display: "flex", flex: 1 }}>
						<Grid container spacing={0} xs={12} sx={{ flex: 3 }}>
							<Grid xs={6} sx={{ flex: 1 }}>
								<Box
									sx={{
										border: adData?.leftImage ? "none" : "4px dashed #ebebeb",
										borderRadius: "sm",
										height: "100%",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										backgroundColor: adData?.leftImage ? "transparent" : "#fafafa"
									}}
								>
									{adData?.leftImage ? (
										<img
											src={adData.leftImage}
											alt="Left advertisement"
											style={{
												maxWidth: "100%",
												maxHeight: "100%",
												width: "auto",
												height: "auto",
												objectFit: "contain",
												borderRadius: "4px"
											}}
										/>
									) : (
										<Typography level="body-sm" sx={{ color: "#999" }}>
											Left Image
										</Typography>
									)}
								</Box>
							</Grid>
							<Grid xs={6} sx={{ flex: 1 }}>
								<Box
									sx={{
										border: adData?.rightImage ? "none" : "4px dashed #ebebeb",
										borderRadius: "sm",
										height: "100%",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										backgroundColor: adData?.rightImage ? "transparent" : "#fafafa"
									}}
								>
									{adData?.rightImage ? (
										<img
											src={adData.rightImage}
											alt="Right advertisement"
											style={{
												maxWidth: "100%",
												maxHeight: "100%",
												width: "auto",
												height: "auto",
												objectFit: "contain",
												borderRadius: "4px"
											}}
										/>
									) : (
										<Typography level="body-sm" sx={{ color: "#999" }}>
											Right Image
										</Typography>
									)}
								</Box>
							</Grid>
						</Grid>
					</Grid>

					{/* Bottom long box */}
					<Grid container spacing={0} xs={12} sx={{ flex: "none", maxHeight: "30vh", overflow: "hidden" }}>
						<Grid xs={12} sx={{ height: "100%" }}>
							<Box
								sx={{
									border: adData?.bottomImage ? "none" : "4px dashed #ebebeb",
									borderRadius: "sm",
									height: "100%",
									width: "100%",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									backgroundColor: adData?.bottomImage ? "transparent" : "#fafafa",
									overflow: "hidden"
								}}
							>
								{adData?.bottomImage ? (
									<img
										src={adData.bottomImage}
										alt="Bottom advertisement"
										style={{
											maxWidth: "100%",
											maxHeight: "100%",
											width: "auto",
											height: "auto",
											objectFit: "contain",
											borderRadius: "4px"
										}}
									/>
								) : (
									<Typography level="body-sm" sx={{ color: "#999" }}>
										Bottom Image
									</Typography>
								)}
							</Box>
						</Grid>
					</Grid>

					{/* Scrolling text */}
					<Grid container spacing={0} xs={12} sx={{ flex: 1, maxHeight: "60px", overflowX: "hidden" }}>
						<Grid
							sx={{
								overflow: 'hidden',
								whiteSpace: 'nowrap',
								position: 'relative',
								'@keyframes scrollText': {
									'0%': { transform: 'translateX(100%)' },
									'100%': { transform: 'translateX(-100%)' }
								}
							}}
							container
							xs={12}
						>
							<Typography
								level="body-lg"
								sx={{
									animation: 'scrollText 30s linear infinite',
									display: 'inline-block',
									whiteSpace: 'nowrap',
									minWidth: '100%',
									fontSize: '2em'
								}}
							>
								{adData?.scrollingText || "No scrolling text provided"}
							</Typography>
						</Grid>
					</Grid>
				</Grid>
			</Box>
		</Box>
	);
}